import {
    Copy,
    Eraser,
    Layers,
    Mic,
    MoreHorizontal,
    Redo2,
    Save,
    Settings2,
    Undo2,
    Wand2,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { PROMPT, TOUCH } from "~/shared/lib/design-tokens";
import { cn } from "~/shared/lib/utils";
import { Button } from "~/shared/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "~/shared/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/shared/ui/tooltip";

import { AttachMenu } from "./AttachMenu";
import { SendButton } from "./SendButton";

const TB = cn(TOUCH.secondary, "text-zinc-500 hover:text-zinc-300 hover:bg-white/5");

interface PromptToolbarProps {
    chatId: string | null;
    disabled: boolean;
    /** Есть ли что очищать: текст промпта, вложения или выбранные элементы. */
    canClear: boolean;
    onClear: () => void;
    onToggleSettings: () => void;
    // attach menu
    supportsAnyAttachment: boolean;
    allSlotsFull: boolean;
    maxImages: number | null;
    currentImagesCount: number;
    onPickFiles: (files: File[]) => void | Promise<void>;
    accept: string | undefined;
    onOpenUploadedPicker: () => void;
    onOpenGeneratedPicker: () => void;
    // send
    costLabel: string;
    canSend: boolean;
    /** Почему кнопка не работает — готовый текст подсказки; null, когда она рабочая. */
    blockedHint: string | null;
    onSend: () => void;
}

/**
 * Нижний ряд промпт-бокса: attach/more слева, settings + send справа.
 *
 * Меню «ещё» показывается всегда. Раньше оно появлялось только после первого
 * фокуса промпта, и после перезагрузки страницы восстановленный черновик нельзя
 * было очистить, пока не кликнешь в поле — единственный способ убрать кадры и
 * элементы оказывался спрятан.
 */
export function PromptToolbar({
    chatId,
    disabled,
    canClear,
    onClear,
    onToggleSettings,
    supportsAnyAttachment,
    allSlotsFull,
    maxImages,
    currentImagesCount,
    onPickFiles,
    accept,
    onOpenUploadedPicker,
    onOpenGeneratedPicker,
    costLabel,
    canSend,
    blockedHint,
    onSend,
}: PromptToolbarProps) {
    const { t } = useTranslation("chat");
    const attachDisabled = !chatId || disabled || !supportsAnyAttachment;
    return (
        <div className="flex items-center border-t border-white/5 px-3 py-2">
            <div className={cn("flex items-center", PROMPT.toolbarGap)}>
                <AttachMenu
                    disabled={attachDisabled}
                    triggerDisabled={attachDisabled}
                    maxImages={maxImages}
                    currentImagesCount={currentImagesCount}
                    onPickFiles={onPickFiles}
                    accept={accept}
                    onPickFromUploaded={chatId ? onOpenUploadedPicker : undefined}
                    pickFromUploadedDisabled={!supportsAnyAttachment || allSlotsFull}
                    onPickFromGenerated={chatId ? onOpenGeneratedPicker : undefined}
                    pickFromGeneratedDisabled={!supportsAnyAttachment || allSlotsFull}
                />
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t("prompt.dictate")}
                            disabled
                            className={TB}
                        >
                            <Mic className="h-4 w-4" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("prompt.dictateComingSoon")}</TooltipContent>
                </Tooltip>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className={TB} aria-label={t("prompt.more.menu")}>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                        <DropdownMenuItem disabled>
                            <Layers className="mr-2 h-4 w-4" />
                            {t("prompt.more.assets")}
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled>
                            <Wand2 className="mr-2 h-4 w-4" />
                            {t("prompt.more.enhance")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem disabled>
                            <Undo2 className="mr-2 h-4 w-4" />
                            {t("prompt.more.undo")}
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled>
                            <Redo2 className="mr-2 h-4 w-4" />
                            {t("prompt.more.redo")}
                        </DropdownMenuItem>
                        {/* Очистка сбрасывает бар целиком: текст, кадры, элементы.
                            Пункт неактивен, когда чистить нечего — иначе он выглядит
                            как действие, которое молча ничего не сделало. */}
                        <DropdownMenuItem disabled={!canClear} onSelect={onClear}>
                            <Eraser className="mr-2 h-4 w-4" />
                            {t("prompt.more.clear")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem disabled>
                            <Save className="mr-2 h-4 w-4" />
                            {t("prompt.more.savePrompt")}
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled>
                            <Copy className="mr-2 h-4 w-4" />
                            {t("prompt.more.copyPrompt")}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            <div className={cn("ml-auto flex items-center", PROMPT.toolbarGap)}>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t("prompt.settings")}
                    onClick={onToggleSettings}
                    className={TB}
                >
                    <Settings2 className="h-4 w-4" />
                </Button>
                <SendButton
                    costLabel={costLabel}
                    canSend={canSend}
                    blockedHint={blockedHint}
                    onSend={onSend}
                />
            </div>
        </div>
    );
}
